import { forwardRef } from 'react';
import { cn } from '../cn.js';

/**
 * The table.
 *
 * Semantic HTML, because a table is one of the few places where the right element does work no
 * styling can replace: a screen reader announces a cell with its column and row headers, and a
 * grid of `div`s announces a wall of text. Everything here is `table.*` tokens — row height,
 * hover, selected, header colours, cell padding — which existed from M0 and nothing used, because
 * there was no Table to use them. Seven files hand-wrote `<table className="w-full
 * border-collapse">` instead, each slightly differently.
 *
 * Density is not a prop. `--table-row-height` moves with `data-density` on the application root,
 * so a compact workspace compacts its tables without a single component knowing about it.
 */
export const Table = forwardRef<HTMLTableElement, React.TableHTMLAttributes<HTMLTableElement>>(
  function Table({ className, ...props }, ref) {
    return (
      /*
       * **The wrapper is the table's surface, and it scrolls.**
       *
       * A bare `<table>` has no background, so on a page it sits directly on `--color-background`
       * and its header — a `surface-sunken` fill — has almost nothing to be seen against. Giving
       * the table its own surface, border and radius makes it a contained thing in both themes and
       * gives the header something to sit on. It scrolls rather than the page, because an
       * operational table is wide and the rest of the screen should not move because one column is.
       *
       * `overflow-hidden` on the same element as the radius, or the first header cell's corner
       * squares off over it.
       *
       * **`min-w-0`, which is the half that was missing.** `overflow-x-auto` only scrolls an
       * element that is allowed to be narrower than its contents, and a flex item's automatic
       * minimum size is its min-content width — so the wrapper refused to shrink, the table was
       * 818px wide on a 320px screen, and the **page** scrolled sideways instead of the table.
       * Measured on the queue board in M4.5-11; it was never the board's bug, it was this one's.
       *
       * **`relative`, for the same measurement's second finding.** An `sr-only` label is
       * `position: absolute`, and an absolutely positioned element inside a scroll container that
       * is not itself positioned escapes to the initial containing block — at its *static*
       * position, which is wherever it sat in the scrolled-out content. A one-pixel hidden span
       * in a cell 700px along therefore put a box at x=733 on a 320px screen and the **document**
       * grew to fit it: a page scrolling sideways because of something no one can see. This makes
       * the wrapper the containing block, which a scroll container should be regardless.
       */
      <div className="relative w-full min-w-0 overflow-x-auto overflow-y-hidden rounded-(--radius-md) border border-(--color-border) bg-(--color-surface)">
        <table
          ref={ref}
          className={cn(
            'w-full border-collapse text-(length:--font-size-body) text-(--color-text-primary)',
            className,
          )}
          {...props}
        />
      </div>
    );
  },
);

export const TableHeader = forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(function TableHeader({ className, ...props }, ref) {
  return <thead ref={ref} className={cn('[&_tr]:border-b', className)} {...props} />;
});

export const TableBody = forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(function TableBody({ className, ...props }, ref) {
  return <tbody ref={ref} className={className} {...props} />;
});

export interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  /** A selected row is also marked `aria-selected`, because colour is never the only signal. */
  selected?: boolean;
  /** Only set this where the whole row is genuinely activatable; it changes the cursor. */
  interactive?: boolean;
}

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(function TableRow(
  { className, selected = false, interactive = false, ...props },
  ref,
) {
  return (
    <tr
      ref={ref}
      aria-selected={selected || undefined}
      data-selected={selected || undefined}
      className={cn(
        // The container draws the outer edge, so the last row does not draw a second one.
        'border-b border-(--color-border) last:border-b-0',
        'transition-colors duration-(--motion-duration-fast) motion-reduce:transition-none',
        interactive && 'cursor-pointer hover:bg-(--table-row-hover)',
        selected && 'bg-(--table-row-selected)',
        className,
      )}
      {...props}
    />
  );
});

export interface TableHeadProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  /** Where the column is sorted, so the header can say so to a screen reader as well as show it. */
  sort?: 'ascending' | 'descending' | 'none';
}

export const TableHead = forwardRef<HTMLTableCellElement, TableHeadProps>(function TableHead(
  { className, sort, scope = 'col', ...props },
  ref,
) {
  return (
    <th
      ref={ref}
      scope={scope}
      aria-sort={sort}
      className={cn(
        'h-(--table-row-height) px-(--table-cell-padding-inline)',
        'text-left align-middle font-medium whitespace-nowrap',
        'bg-(--table-header-background) text-(--table-header-color)',
        'text-(length:--font-size-caption)',
        className,
      )}
      {...props}
    />
  );
});

export const TableCell = forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(function TableCell({ className, ...props }, ref) {
  return (
    <td
      ref={ref}
      className={cn(
        'h-(--table-row-height) px-(--table-cell-padding-inline) align-middle',
        className,
      )}
      {...props}
    />
  );
});

/**
 * The cell that names its row.
 *
 * A `<th scope="row">`, which is what makes a screen reader say "Support queue, waiting, four"
 * rather than reading four numbers with no subject. It is a *cell* and not a column header, so it
 * takes the body's background and the row's hover: `TableHead` would paint the header fill down
 * the first column and make the table look like it had two header strips.
 *
 * The weight is the only thing that marks it, which is the whole intent — the row's subject should
 * read as the subject without becoming a second heading.
 */
export const TableRowHeader = forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(function TableRowHeader({ className, scope = 'row', ...props }, ref) {
  return (
    <th
      ref={ref}
      scope={scope}
      className={cn(
        'h-(--table-row-height) px-(--table-cell-padding-inline)',
        'text-left align-middle font-medium text-(--color-text-primary)',
        className,
      )}
      {...props}
    />
  );
});

/**
 * The table's accessible name.
 *
 * Visually hidden by default rather than absent: a table needs a name, and most operational tables
 * already have a heading above them that would read twice if this were visible.
 */
export const TableCaption = forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(function TableCaption({ className, ...props }, ref) {
  return <caption ref={ref} className={cn('sr-only', className)} {...props} />;
});
